import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { IoSearchOutline, IoMapOutline, IoBusinessOutline, IoChevronForward, IoLocationOutline, IoAddCircleOutline, IoPeople, IoPricetags } from 'react-icons/io5';
import { SelectPicker, Loader, RandomImage } from '../../../components';
import api from '../../../services/api';
import ToastService from '../../../services/toast.service';
import useCentreSearch, { centreSearchFilterOption } from '../hooks/useCentreSearch';
import useRoles from '../../../hooks/useRoles';
import { useAuthenticationService } from '../../../services/authentication.service';
import { ROLE_CENTRE_READONLY, ROLE_CENTRE_READWRITE, ROLE_CENTRE_SUPERADMIN } from '../../../utils/constants/roles';

const CentreMapView = lazy(() => import('./CentreMapView'));
const AddCentreForm = lazy(() => import('./AddCentreForm'));

function StaffTooltipAvatar({ staff, initials }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const photoId = staff?.externalId;

  useEffect(() => {
    if (!photoId) {
      setLoading(false);
      return;
    }

    let active = true;
    let objectUrl;
    const controller = new AbortController();
    setLoading(true);

    api.getEntraStaffPhoto(photoId, controller.signal)
      .then((blob) => {
        if (!active || !(blob instanceof Blob) || blob.size === 0) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId]);

  return (
    <span
      aria-hidden="true"
      style={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(255,255,255,0.12)',
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 30px',
      }}
    >
      {loading
        ? <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%' }} />
        : url
          ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : initials}
    </span>
  );
}

function getStatusColor(status) {
  if (!status) return 'blue';
  const s = status.toLowerCase();
  if (s.includes('open') || s.includes('active')) return 'green';
  if (s.includes('closed') || s.includes('inactive')) return 'red';
  if (s.includes('pending')) return 'orange';
  return 'blue';
}

const centreSearchStyles = {
  option: (base, { isDisabled, isFocused, isSelected, data }) => ({
    ...base,
    display: data?.hideOption ? 'none' : base.display,
    backgroundColor: isDisabled
      ? 'transparent'
      : isSelected
      ? 'var(--primary-color)'
      : isFocused
      ? 'rgba(0, 109, 158, 0.18)'
      : 'var(--background-color)',
    color: isDisabled
      ? '#8e8e93'
      : isSelected
      ? 'white'
      : 'var(--text-color)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    ':active': {
      backgroundColor: isSelected ? 'var(--primary-color)' : 'rgba(0, 109, 158, 0.18)',
    },
  }),
  group: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  groupHeading: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
};

export default function CentreSearch() {
  const navigate = useNavigate();
  const auth = useAuthenticationService();
  const { canCreateCentre, hasPropertyAccess } = useRoles();
  const [activeView, setActiveView] = useState('search');
  const [searchInput, setSearchInput] = useState('');
  const [staffTooltip, setStaffTooltip] = useState(null);
  const [tagTooltip, setTagTooltip] = useState(null);
  const { centreOptions, centreMapItems, loading } = useCentreSearch();
  const hasCentreStaffAccess = auth.isInAnyRole([ROLE_CENTRE_READONLY, ROLE_CENTRE_READWRITE, ROLE_CENTRE_SUPERADMIN]);

  const openCentre = useCallback((centreId) => {
    if (centreId) {
      navigate(`/centre/${centreId}`);
      return;
    }

    ToastService.warn('Unable to open centre details');
  }, [navigate]);

  const handleCentreCreated = useCallback((centreId) => {
    if (centreId) {
      navigate(`/centre/${centreId}`);
      return;
    }

    ToastService.warn('Centre created but could not open details');
  }, [navigate]);

  const openProperty = useCallback((centreId, propertyId) => {
    if (centreId && propertyId) {
      navigate(`/centre/${centreId}?tab=properties&propertyId=${propertyId}`);
      return;
    }

    ToastService.warn('Unable to open centre details');
  }, [navigate]);

  const handleChange = useCallback((option) => {
    if (!option) return;
    if (option.hideOption) return;
    if (option.propertyId && option.centreId) {
      openProperty(option.centreId, option.propertyId);
    } else if (option.centreId) {
      openCentre(option.centreId);
    } else {
      ToastService.warn('Unable to open centre details');
    }
  }, [openCentre, openProperty]);

  const staffMatchesSearch = useCallback((staff) => {
    const words = searchInput.toLowerCase().split(' ').filter(Boolean);
    if (!words.length) return false;

    const staffTags = (staff?.tags ?? []).join(' ');
    const haystack = [staff?.staffName, staff?.roleName, staffTags].filter(Boolean).join(' ').toLowerCase();
    return words.every((w) => haystack.includes(w));
  }, [searchInput]);

  const hasActiveSearch = Boolean(searchInput.trim());

  const openStaffTooltip = useCallback((event, group) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 340;
    const viewportPadding = 12;
    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - tooltipWidth, window.innerWidth - tooltipWidth - viewportPadding),
    );
    setTagTooltip(null);
    setStaffTooltip({
      key: group?.centreId ?? group?.label,
      centreId: group?.centreId ?? null,
      left,
      top: rect.bottom + 8,
      staff: group?.centreStaff ?? [],
    });
  }, []);

  const closeStaffTooltip = useCallback(() => {
    setStaffTooltip(null);
  }, []);

  const openStaffTab = useCallback((centreId) => {
    if (!centreId) {
      ToastService.warn('Unable to open staff details');
      return;
    }
    navigate(`/centre/${centreId}?tab=staff`);
    closeStaffTooltip();
  }, [navigate, closeStaffTooltip]);

  const openTagTooltip = useCallback((event, group) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 280;
    const viewportPadding = 12;
    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - tooltipWidth, window.innerWidth - tooltipWidth - viewportPadding),
    );
    setStaffTooltip(null);
    setTagTooltip({
      key: group?.centreId ?? group?.label,
      left,
      top: rect.bottom + 8,
      tags: group?.centreTags ?? [],
      label: group?.label ?? 'Centre',
    });
  }, []);

  const closeTagTooltip = useCallback(() => {
    setTagTooltip(null);
  }, []);

  useEffect(() => {
    if (!staffTooltip && !tagTooltip) return;
    const handleMouseDown = (event) => {
      const target = event.target;
      if (
        target?.closest?.('[data-staff-tooltip]')
        || target?.closest?.('[data-staff-trigger]')
        || target?.closest?.('[data-tag-tooltip]')
        || target?.closest?.('[data-tag-trigger]')
      ) return;
      setStaffTooltip(null);
      setTagTooltip(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [staffTooltip, tagTooltip]);

  const getStaffInitials = useCallback((name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
  }, []);

  const formatGroupLabel = useCallback((group) => {
    const propertyCount = group.options.filter((opt) => !opt.hideOption).length;
    const hasStaff = Boolean(group?.centreStaff?.length);
    const tags = group?.centreTags ?? [];
    const hasTags = tags.length > 0;
    const isStaffOpen = staffTooltip?.key === (group?.centreId ?? group?.label);
    const isTagsOpen = tagTooltip?.key === (group?.centreId ?? group?.label);
    return (
      <div
        className="centre-search-group-header"
        data-testid={`${group.label}-group`}
        role="button"
        tabIndex={0}
        aria-label={`Open ${group.label} centre`}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (group.centreId) {
            openCentre(group.centreId);
          }
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && group.centreId) {
            e.preventDefault();
            openCentre(group.centreId);
          }
        }}
      >
        <RandomImage seed={group.label} height={72} className="centre-search-group-doodle" aria-hidden="true" />
        <span className="centre-search-group-left">
          <span className="centre-search-group-text">
            <span className="centre-search-group-name">
              <IoBusinessOutline className="centre-search-group-icon" aria-hidden="true" />
              {group.label}
            </span>
            {hasPropertyAccess && (
              <span className="centre-search-group-sub">
                {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
              </span>
            )}
          </span>
        </span>
        <span className="centre-search-group-right">
          {hasCentreStaffAccess && hasStaff && (
            <span
              data-staff-trigger="true"
              role="button"
              tabIndex={0}
              aria-label={`Show ${group.centreStaff.length} staff at ${group.label}`}
              aria-expanded={isStaffOpen}
              className={`centre-search-chip ${isStaffOpen ? 'centre-search-chip--active' : ''}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                if (isStaffOpen) {
                  closeStaffTooltip();
                } else {
                  openStaffTooltip(e, group);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isStaffOpen) {
                    closeStaffTooltip();
                  } else {
                    openStaffTooltip(e, group);
                  }
                }
              }}
            >
              <IoPeople aria-hidden="true" />
              <span>{group.centreStaff.length}</span>
              <IoChevronForward aria-hidden="true" className="centre-search-chip-caret" style={{ transform: isStaffOpen ? 'rotate(90deg)' : 'none' }} />
            </span>
          )}
          {hasTags && (
            <span
              data-tag-trigger="true"
              role="button"
              tabIndex={0}
              aria-label={`Show ${tags.length} tags for ${group.label}`}
              aria-expanded={isTagsOpen}
              className={`centre-search-chip ${isTagsOpen ? 'centre-search-chip--active' : ''}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                if (isTagsOpen) {
                  closeTagTooltip();
                } else {
                  openTagTooltip(e, group);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isTagsOpen) {
                    closeTagTooltip();
                  } else {
                    openTagTooltip(e, group);
                  }
                }
              }}
            >
              <IoPricetags aria-hidden="true" />
              <span>{tags.length}</span>
              <IoChevronForward aria-hidden="true" className="centre-search-chip-caret" style={{ transform: isTagsOpen ? 'rotate(90deg)' : 'none' }} />
            </span>
          )}
          <IoChevronForward aria-hidden="true" className="centre-search-group-go" />
        </span>
      </div>
    );
  }, [openCentre, hasPropertyAccess, hasCentreStaffAccess, closeStaffTooltip, openStaffTooltip, staffTooltip, tagTooltip, closeTagTooltip, openTagTooltip]);

  const getOptionLabel = useCallback((option) => {
    if (!option) return null;
    if (option.hideOption) return null;
    const isProperty = Boolean(option.propertyId);
    return (
      <div
        className={`centre-search-option ${isProperty ? 'centre-search-option--property' : 'centre-search-option--centre'}`}
        data-testid="centre-search-item"
      >
        <div className="centre-search-item-left">
          <span className="centre-search-item-name">
            {isProperty && <IoLocationOutline className="centre-search-item-icon" aria-hidden="true" />}
            {option.propertyName || option.buildingTypeName || option.centreDisplayName || option.centreName || 'Property'}
          </span>
          <span className="centre-search-item-meta">
            {[option.address?.address1, option.address?.city, option.address?.postalCode].filter(Boolean).join(', ')}
          </span>
        </div>
        <div className="centre-search-item-right">
          <span className={`centre-pill centre-pill--${getStatusColor(option.propertyStatusDescription || option.currentStatus)}`}>
            {option.propertyStatusDescription || option.currentStatus || 'Unknown'}
          </span>
          <IoChevronForward className="centre-search-item-chevron" aria-hidden="true" />
        </div>
      </div>
    );
  }, []);

  return (
    <div className="centre-search-box">
      <nav className="centre-search-tabs" role="tablist" aria-label="Centre search views">
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'search'}
          className={`centre-search-tab ${activeView === 'search' ? 'centre-search-tab--active' : ''}`}
          onClick={() => setActiveView('search')}
        >
          <IoSearchOutline /> Search
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'map'}
          className={`centre-search-tab ${activeView === 'map' ? 'centre-search-tab--active' : ''}`}
          onClick={() => setActiveView('map')}
        >
          <IoMapOutline /> Map
        </button>
        {canCreateCentre && (
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'add'}
            className={`centre-search-tab ${activeView === 'add' ? 'centre-search-tab--active' : ''}`}
            onClick={() => setActiveView('add')}
          >
            <IoAddCircleOutline /> Add Centre
          </button>
        )}
      </nav>

      {activeView === 'search' && (
        <SelectPicker
          data={centreOptions}
          onChange={handleChange}
          filterOption={centreSearchFilterOption}
          getOptionLabel={getOptionLabel}
          formatGroupLabel={formatGroupLabel}
          placeholder="Search by centre name, area, region, address..."
          aria-label="Search centres by name, area, region or address"
          isClearable
          loading={loading}
          value={null}
          testid="centre-search-input"
          noOptionsMessage={() => 'No centres match your search'}
          style={centreSearchStyles}
          className="centre-search-select"
          onInputChange={(value, meta) => {
            if (meta?.action === 'input-change') {
              setSearchInput(value || '');
            }
            if (meta?.action === 'menu-close' || meta?.action === 'set-value' || meta?.action === 'input-blur') {
              setSearchInput('');
            }
          }}
        />
      )}

      {activeView === 'map' && (
        <Suspense fallback={<div className="centre-map-state"><Loader content="Loading map..." /></div>}>
          <CentreMapView
            centres={centreMapItems}
            onOpenCentre={openCentre}
            onOpenProperty={openProperty}
          />
        </Suspense>
      )}

      {activeView === 'add' && canCreateCentre && (
        <Suspense fallback={<div className="centre-map-state"><Loader content="Loading..." /></div>}>
          <AddCentreForm onCreated={handleCentreCreated} />
        </Suspense>
      )}

      {loading && (
        <Loader content="Loading centres..." fullscreen />
      )}

      {staffTooltip && typeof document !== 'undefined' && createPortal(
        <div
          data-staff-tooltip="true"
          role="tooltip"
          style={{
            position: 'fixed',
            left: staffTooltip.left,
            top: staffTooltip.top,
            width: 340,
            maxHeight: '56vh',
            overflowY: 'auto',
            background: '#0f1720',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 10,
            padding: 8,
            zIndex: 1000000,
            boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
          }}
          onMouseEnter={(e) => e.stopPropagation()}
        >
          {staffTooltip.staff.map((staff) => {
            const isMatch = hasActiveSearch && staffMatchesSearch(staff);
            const roleColor = staff.roleRgb || null;
            return (
              <button
                type="button"
                key={`${staff.externalId}-${staff.roleName}`}
                aria-label={`Open ${staff.staffName} staff details`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  marginBottom: 6,
                  background: isMatch ? 'rgba(0, 173, 95, 0.22)' : 'rgba(255,255,255,0.04)',
                  border: isMatch ? '1px solid rgba(0, 173, 95, 0.55)' : '1px solid transparent',
                  borderLeft: roleColor ? `4px solid ${roleColor}` : (isMatch ? '1px solid rgba(0, 173, 95, 0.55)' : '1px solid transparent'),
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
                onClick={() => openStaffTab(staffTooltip.centreId)}
              >
                <StaffTooltipAvatar staff={staff} initials={getStaffInitials(staff.staffName)} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ color: 'white', fontWeight: 700 }}>{staff.staffName}</span>
                  {staff.roleName && <span style={{ color: roleColor || '#8fd1ff', fontSize: 12, fontWeight: 600 }}>{staff.roleName}</span>}
                  {staff.tags?.length > 0 && (
                    <span style={{ color: '#ffd166', fontSize: 12 }}>{staff.tags.join(', ')}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}

      {tagTooltip && typeof document !== 'undefined' && createPortal(
        <div
          data-tag-tooltip="true"
          role="tooltip"
          aria-label={`${tagTooltip.label} tags`}
          style={{
            position: 'fixed',
            left: tagTooltip.left,
            top: tagTooltip.top,
            width: 280,
            maxHeight: '42vh',
            overflowY: 'auto',
            background: '#0f1720',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 10,
            padding: 10,
            zIndex: 1000000,
            boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {tagTooltip.tags.map((tag) => (
            <span key={`${tagTooltip.key}-${tag}`} className="centre-pill centre-pill--dark">
              {tag}
            </span>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
